import { HttpClient } from '@angular/common/http';
import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { appConfigService } from '@app/config';
import { TreeViewProviderDirective } from '@app/providers';
import { ArhivPosjednik } from '@app/shared/models/arhiv-posjednik';
import { JedinicaGradiva } from '@app/shared/models/jedinica-gradiva';
import { ARHIV_POSJEDNIK } from '@app/shared/models/types';
import {
  AttributeStateService,
  AuthRequestOptions,
  BackendDataSourceService,
  FilterViewProviderDirective,
  ListViewProviderDirective,
  UIComponents
} from '@ntap/core';
import { Message, TreeDragDropService, TreeNode } from 'primeng/api';
import {
  debounceTime,
  first,
  map,
  Observable,
  of,
  Subscription,
  switchMap,
  tap
} from 'rxjs';
import { PremjestiService } from '../actions/premjesti-action/premjesti.service';
import { HierarhijaComponent } from '../hierarhija/hierarhija.component';
import { EarhivTreeNode } from '../hierarhija/types';
import { ParentListViewProvider } from '../pretraga-kataloga/providers/parent-list-view.provider';
import {
  buildSearchPayload,
  filterExcluded,
  validateSearchInput
} from './jedinica-hierarchy-picker.utils';
import { isRootJedinica } from '@app/shared/util/common-utils';

export type UpisanaOznakaDTO = { redoslijed: number };
export type PrijedlogArhivskeOznakeDTO = {
  prviMinimalniRedoslijed: number;
  zadnjiMinimalniRedoslijed: number;
  postojiRedoslijed: boolean;
  zakljucano: boolean;
};

export type BatchLockCheckDTO = {
  zakljucano: boolean;
  zakljucaneOznake: string[];
  cyclicDependency: boolean;
  cyclicDependencyMessage: string;
};

export interface JedinicaSearchResult {
  id: number;
  arhivskaOznaka: string;
  glavniNaziv: string;
  razina: string;
}

@Component({
  selector: 'app-jedinica-hierarchy-picker',
  templateUrl: './jedinica-hierarchy-picker.component.html',
  styleUrls: ['./jedinica-hierarchy-picker.component.scss'],
  providers: [
    TreeDragDropService,
    TreeViewProviderDirective,
    FilterViewProviderDirective,
    {
      provide: ListViewProviderDirective,
      useExisting: TreeViewProviderDirective
    }
  ]
})
export class JedinicaHierarchyPickerComponent
  extends HierarhijaComponent
  implements OnInit, OnDestroy
{
  private viewProvider = inject(TreeViewProviderDirective);
  private httpClient = inject(HttpClient);
  private config = inject(appConfigService);
  private parentListView = inject(ParentListViewProvider);
  private fb = inject(FormBuilder);
  private premjestiService = inject(PremjestiService);
  private backendDataSourceService = inject(BackendDataSourceService);
  private attributeStateService = inject(AttributeStateService);

  public formGroup$: Observable<FormGroup>;
  public view$ = this.viewProvider.view$;
  public fg = this.fb.group({
    redoslijed: this.fb.control<number | null>({ value: null, disabled: false })
  });

  public redoslijedFormControl: FormControl<number | null> | null;
  public newArhivskaOznakaPrefix: string | null;
  public postojiRedoslijed: boolean;
  public warnMessage: Message[];

  actionBarCompId = UIComponents.ACTIONBAR;
  actions$: Observable<any[]>;

  override filterViewName = 'filter_strukture_gradiva_premjestanje';
  override nodesViewName = 'jedinica_gradiva_premjestanje_odabir';

  private defaultWarnMessage = [
    {
      severity: 'info',
      detail:
        'Jeste li sigurni da želite premjestiti jedinicu gradiva uključujući i sve jedinice gradiva na nižim razinama?'
    }
  ];
  selectedNewParentJedinicaId: number | null = null;
  private selectedParentIdsOfParentIds: number[];
  selectedParentIds: number[];
  private rootId: number | null = null;
  private allParentsLevelZbirke = false;

  selectedParentName: string;
  selectedParent: JedinicaGradiva;
  selectedParents: JedinicaGradiva[] = [];
  isBatchMode = false;

  searchQuery: string = '';
  searchResults: any[] = [];
  isSearching: boolean = false;
  showSearchResults: boolean = false;
  searchError: string | null = null;
  validationError: string | null = null;
  private searchSubscription: Subscription | null = null;
  private razinaMap: Map<number, string> = new Map();

  override ngOnInit() {
    this.rootNodesSelectable = true;
    super.ngOnInit();
    this.warnMessage = this.defaultWarnMessage;
    this.redoslijedFormControl = this.fg.get('redoslijed') as FormControl<
      number | null
    >;

    // Dohvati allowed values za razinu (NTAP način - resolvira FK ID u naziv)
    this.viewProvider.view$.pipe(first()).subscribe((view) => {
      const razinaAttr = view.attributes?.find(
        (a: any) => a.name === 'en~jedinica_gradiva~razina'
      );
      if (razinaAttr) {
        this.attributeStateService
          .getAllowedValues(razinaAttr, {}, this.viewProvider.viewName)
          .subscribe((avs) => {
            avs.forEach((av) => this.razinaMap.set(Number(av.value), av.label));
          });
      }
    });

    this.addSubscription(
      this.viewProvider.selectedItems$
        .pipe(
          tap((selected) => {
            if (!!selected && selected.length > 0) {
              const selectedItem: any = selected[0];
              this.newArhivskaOznakaPrefix =
                selectedItem['en~jedinica_gradiva~arhivska_oznaka'] ||
                selectedItem['en~arhiv_posjednik~oznaka'];
              this.selectedNewParentJedinicaId =
                selectedItem['en~jedinica_gradiva~id'] || null;

              if (
                isRootJedinica(this.selectedParent) ||
                this.selectedParents.some((p) => isRootJedinica(p))
              ) {
                this.redoslijedFormControl?.disable();
              } else {
                this.redoslijedFormControl?.enable();
              }

              this.redoslijedOznakaChange({
                value: null // oznacena nova jedinica, prema tome reset redoslijeda
              });
            } else {
              this.newArhivskaOznakaPrefix = null;
              this.redoslijedFormControl?.enable();
              this.redoslijedFormControl?.setValue(null); // unselect
            }
          })
        )
        .subscribe()
    );
    // TODO ovaj dio nije dobar, treba to nekako drugacije rijesiti, ne bi trebali
    // drzati ovaj state interno, a komponentu ne bi trebalo zanimati
    // koja vrsta providera je parent
    this.addSubscription(
      this.parentListView.selectedItems$
        .pipe(
          first(),
          tap((selectedParents) => {
            const selectedJedinice = selectedParents as JedinicaGradiva[];
            this.isBatchMode = selectedJedinice.length > 1;
            if (this.isBatchMode) {
              this.premjestiService.setBatchMode(selectedJedinice.length);
            }
            this.selectedParents = selectedJedinice;
            this.selectedParentIdsOfParentIds = selectedJedinice.map(
              (parent) =>
                parent['en~jedinica_gradiva~nadredena_jedinica_gradiva']
            );
            this.selectedParentIds = selectedJedinice.map(
              (parent) => parent['en~jedinica_gradiva~id']
            );
            this.selectedParentName = this.isBatchMode
              ? `${selectedJedinice.length} jedinica gradiva`
              : selectedJedinice.map(
                  (parent) =>
                    parent['en~jedinica_gradiva~arhivska_oznaka'] +
                    ' ' +
                    parent['en~jedinica_gradiva~glavni_naziv']
                )[0];
            this.selectedParent = selectedJedinice[0];
            this.allParentsLevelZbirke = selectedJedinice.every(
              (p) => !p['en~jedinica_gradiva~nadredena_jedinica_gradiva']
            );
            this.rootNodesSelectable = !this.allParentsLevelZbirke;
            if (this.isBatchMode) {
              this.defaultWarnMessage = [
                {
                  severity: 'info',
                  detail: `Jeste li sigurni da želite premjestiti ${selectedJedinice.length} jedinica gradiva uključujući sve jedinice na nižim razinama?`
                }
              ];
              this.warnMessage = this.defaultWarnMessage;
            }
          })
        )
        .subscribe()
    );
    const superMapNodes = this.viewProvider.mapNodes;
    this.viewProvider.mapNodes = (
      response: any,
      parentNode?: TreeNode
    ): EarhivTreeNode[] => {
      return superMapNodes(response, parentNode).map((node) => {
        if (
          this.selectedParentIds.indexOf(node.id) >= 0 ||
          this.selectedParentIdsOfParentIds.indexOf(node.id) >= 0 ||
          this.checkParentInHierarchyIsCurrentlySelected(
            node,
            (current, selected) =>
              !!current?.data &&
              !!selected &&
              current?.data['en~jedinica_gradiva~id'] ===
                selected['en~jedinica_gradiva~id']
          )
        ) {
          node.selectable = false;
        }
        return node;
      });
    };
    this.actions$ = this.viewProvider.view$.pipe(map((view) => view.actions));
    this.addSubscription(
      this.premjestiService.redoslijedChange$
        .pipe(
          debounceTime(300),
          switchMap((value: number) => {
            if (!this.selectedNewParentJedinicaId && !this.rootId) {
              return of({}); // TODO ne bi trebalo trebati ali negdje se firea dvaput ?
            }
            if (this.isBatchMode) {
              return this.performBatchLockCheck();
            }
            let url = `${this.config.apiHost}move/suggestion?jedinicaId=${
              this.selectedParent['en~jedinica_gradiva~id']
            }&newParentJedinicaId=${
              this.selectedNewParentJedinicaId || ''
            }&katalogId=${this.rootId || ''}`;
            const noviRedoslijed = value;
            if (noviRedoslijed) {
              url += `&redoslijed=${noviRedoslijed}`;
            }
            return this.httpClient
              .get<PrijedlogArhivskeOznakeDTO>(url, new AuthRequestOptions())
              .pipe(
                tap((response) => {
                  if (response.zakljucano) {
                    this.warnMessage = [
                      {
                        severity: 'error',
                        detail:
                          'Odabrali ste zaključanu jedinicu gradiva ili su neke od njezinih podrazina zaključane.'
                      }
                    ];
                  } else {
                    this.postojiRedoslijed = response.postojiRedoslijed;
                    const validiraniRedoslijed =
                      noviRedoslijed || response.prviMinimalniRedoslijed;
                    if (response.postojiRedoslijed) {
                      this.warnMessage = [
                        {
                          severity: 'warn',
                          detail:
                            'Jeste li sigurni da želite premjestiti jedinicu gradiva uključujući i sve jedinice gradiva na nižim razinama? Upisali ste redoslijed koji već postoji na odabranoj razini, pa će sve jedinice s postojećim redoslijedom biti renumerirane!'
                        }
                      ];
                    } else {
                      this.warnMessage = this.defaultWarnMessage;
                    }
                    this.redoslijedFormControl?.setValue(validiraniRedoslijed);
                    this.premjestiService.premjestanjeConfigComplete({
                      $redoslijed: validiraniRedoslijed
                    });
                    this.premjestiService.loadingFinished();
                  }
                })
              );
          })
        )
        .subscribe()
    );
  }

  getActionComponentId(action: any) {
    if (!action.ui_component) {
      throw new Error('Cannot determine action compId: ' + action.name);
    }
    return action.ui_component;
  }

  redoslijedOznakaChange(event: { value: any }) {
    this.premjestiService.changeRedoslijed(event.value);
  }

  close() {
    this.premjestiService.openPremjestanjeDialog(false);
  }

  override onNodeSelect(event: { node: TreeNode }) {
    if (event.node.data['$itemType'] === ARHIV_POSJEDNIK) {
      this.rootId = (event.node.data as ArhivPosjednik)[
        'en~arhiv_posjednik~id'
      ];
      this.premjestiService.rootSelected(event.node);
      // Root ArhivPosjednik je uvijek fond/zbirka razina — disable redoslijed
      this.redoslijedFormControl?.disable();
      this.redoslijedOznakaChange({ value: null });
    } else {
      this.rootId = null;
      this.premjestiService.rootSelected({});
    }
  }

  performSearch(): void {
    const validation = validateSearchInput(this.searchQuery);
    if (!validation.valid) {
      this.validationError = validation.error!;
      return;
    }

    this.isSearching = true;
    this.searchError = null;
    this.validationError = null;

    if (this.searchSubscription) {
      this.searchSubscription.unsubscribe();
    }

    const trimmedQuery = this.searchQuery.trim();
    const posjednikId = this.selectedParent['en~jedinica_gradiva~posjednik'];
    const excludeId = this.selectedParent['en~jedinica_gradiva~id'];
    const payload = buildSearchPayload(trimmedQuery, posjednikId);

    this.searchSubscription = this.backendDataSourceService
      .getViewData('jedinica_gradiva', payload)
      .subscribe({
        next: (response: any[]) => {
          this.searchResults = filterExcluded(response, excludeId);
          this.showSearchResults = true;
          this.isSearching = false;
        },
        error: () => {
          this.searchError = 'Pretraga nije uspjela';
          this.isSearching = false;
        }
      });
  }

  isItemDisabled = (item: any): boolean => {
    return (
      this.selectedParentIds.includes(item['en~jedinica_gradiva~id']) ||
      this.selectedParentIdsOfParentIds.includes(
        item['en~jedinica_gradiva~id']
      ) ||
      this.selectedParents
        .map((p) =>
          item['en~jedinica_gradiva~arhivska_oznaka'].startsWith(
            p['en~jedinica_gradiva~arhivska_oznaka']
          )
        )
        .some((i) => i)
    );
  };

  onSearchResultSelect(item: any): void {
    if (this.isItemDisabled(item)) return;

    this.newArhivskaOznakaPrefix = item['en~jedinica_gradiva~arhivska_oznaka'];
    this.selectedNewParentJedinicaId = item['en~jedinica_gradiva~id'];
    this.rootId = null;
    this.premjestiService.rootSelected({});

    // Provjera razine odabranog search rezultata: ako je FOND ili ZBIRKA, disable redoslijed
    if (!item['en~jedinica_gradiva~nadredena_jedinica_gradiva']) {
      this.redoslijedFormControl?.disable();
    } else {
      this.redoslijedFormControl?.enable();
    }

    this.viewProvider.selectedItems = [item];

    this.redoslijedOznakaChange({ value: null });
  }

  clearSearch(): void {
    this.searchQuery = '';
    this.searchResults = [];
    this.showSearchResults = false;
    this.searchError = null;
    this.validationError = null;
    if (this.searchSubscription) {
      this.searchSubscription.unsubscribe();
      this.searchSubscription = null;
    }
  }

  getRazinaLabel(id: number): string {
    return this.razinaMap.get(id) ?? String(id);
  }

  override ngOnDestroy(): void {
    this.searchSubscription?.unsubscribe();
    super.ngOnDestroy();
  }

  private checkParentInHierarchyIsCurrentlySelected(
    obj: TreeNode,
    condition: (
      current: TreeNode | undefined,
      selected: JedinicaGradiva
    ) => boolean
  ) {
    let current: any = obj;
    while (current !== null && current !== undefined) {
      for (const selectedParent of this.selectedParents) {
        if (condition(current, selectedParent)) {
          return true;
        }
      }
      current = current.parent;
    }
    return false;
  }

  private performBatchLockCheck(): Observable<any> {
    const sourceIds = this.selectedParentIds.join(',');
    const targetId = this.selectedNewParentJedinicaId || '';
    const katalogId = this.rootId || '';
    const url = `${this.config.apiHost}move/batch-lock-check?sourceIds=${sourceIds}&targetId=${targetId}&katalogId=${katalogId}`;
    return this.httpClient
      .get<BatchLockCheckDTO>(url, new AuthRequestOptions())
      .pipe(
        tap((response) => {
          if (response.zakljucano || response.cyclicDependency) {
            const detail = response.cyclicDependency
              ? response.cyclicDependencyMessage
              : 'Odabrali ste zaključanu jedinicu gradiva ili su neke od njezinih podrazina zaključane.';
            this.warnMessage = [
              {
                severity: 'error',
                detail
              }
            ];
          } else {
            this.warnMessage = this.defaultWarnMessage;
            this.premjestiService.premjestanjeConfigComplete({
              $redoslijed: null
            });
            this.premjestiService.loadingFinished();
          }
        })
      );
  }
}
